import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import {
    AccountBalance as ETHIcon,
    Token as TokenIcon,
    Pool as PoolIcon,
    Collections as NFTIcon
} from '@mui/icons-material';
import { AssetSummary } from '../../types/DashboardTypes';

interface AssetSummaryCardProps {
    assetSummary: AssetSummary;
    variant?: 'default' | 'compact';
}

const AssetSummaryCard: React.FC<AssetSummaryCardProps> = ({
    assetSummary,
    variant = 'default'
}) => {
    if (variant === 'compact') {
        return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip
                    icon={<ETHIcon />}
                    label={`${parseFloat(assetSummary.totalETH).toFixed(3)} ETH`}
                    size="small"
                    color="primary"
                />
                {assetSummary.totalTokens > 0 && (
                    <Chip
                        icon={<TokenIcon />}
                        label={`${assetSummary.totalTokens} Tokens`}
                        size="small"
                        color="secondary"
                    />
                )}
                {assetSummary.totalLPPositions > 0 && (
                    <Chip
                        icon={<PoolIcon />}
                        label={`${assetSummary.totalLPPositions} LP`}
                        size="small"
                        color="info"
                    />
                )}
                {assetSummary.totalNFTs > 0 && (
                    <Chip
                        icon={<NFTIcon />}
                        label={`${assetSummary.totalNFTs} NFTs`}
                        size="small"
                        color="warning"
                    />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                📊 Asset Summary
            </Typography>
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ETHIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                            {parseFloat(assetSummary.totalETH).toFixed(4)} ETH
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TokenIcon fontSize="small" color="secondary" />
                        <Typography variant="body2">
                            {assetSummary.totalTokens} Tokens
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PoolIcon fontSize="small" color="info" />
                        <Typography variant="body2">
                            {assetSummary.totalLPPositions} LP Positions
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NFTIcon fontSize="small" color="warning" />
                        <Typography variant="body2">
                            {assetSummary.totalNFTs} NFTs
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AssetSummaryCard;
